import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useState, useEffect } from 'react';

// @mui
import { styled } from '@mui/material/styles';
import { Box, Container, Typography } from '@mui/material';

// components
import Page from '../components/Page';
//
import useLocales from '../hooks/useLocales';
import { MaintenanceIllustration } from '../assets';

// Enable UTC plugin
dayjs.extend(utc);

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  paddingTop: theme.spacing(15),
  paddingBottom: theme.spacing(10),
}));

const CountdownBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(2),
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
}));

const TimeBlock = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(2, 3),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.neutral,
  minWidth: 80,
}));

// ----------------------------------------------------------------------

type MaintenanceProps = {
  endTime?: string;
};

type TimeLeft = {
  hours: number;
  minutes: number;
  seconds: number;
};

function calculateTimeLeft(endTime: string): TimeLeft | null {
  // Parse endTime as UTC, compare with current UTC time
  const end = dayjs.utc(endTime);
  const now = dayjs.utc();
  const diff = end.diff(now, 'second');

  if (diff <= 0) {
    return null;
  }

  return {
    hours: Math.floor(diff / 3600),
    minutes: Math.floor((diff % 3600) / 60),
    seconds: diff % 60,
  };
}

export default function Maintenance({ endTime }: MaintenanceProps) {
  const { t } = useLocales();
  const [timeLeft, setTimeLeft] = useState<TimeLeft | null>(
    endTime ? calculateTimeLeft(endTime) : null
  );

  useEffect(() => {
    if (!endTime) return;

    const interval = setInterval(() => {
      const newTimeLeft = calculateTimeLeft(endTime);
      setTimeLeft(newTimeLeft);

      // Reload page when maintenance ends
      if (!newTimeLeft) {
        window.location.reload();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [endTime]);

  const formatNumber = (num: number) => String(num).padStart(2, '0');

  return (
    <Page title={t('maintenance.title')} sx={{ height: 1 }}>
      <RootStyle>
        <Container sx={{ textAlign: 'center' }}>
          <Typography
            variant="h3"
            sx={{
              marginBottom: '16px',
            }}
          >
            {t('maintenance.title')}
          </Typography>
          <Typography sx={{ color: 'text.secondary' }}>{t('maintenance.description')}</Typography>

          <MaintenanceIllustration sx={{ my: 6, height: 240 }} />

          {timeLeft && (
            <>
              <Typography variant="h6" sx={{ color: 'text.secondary', mb: 2 }}>
                {t('maintenance.estimatedEnd')}
              </Typography>
              <CountdownBox>
                <TimeBlock>
                  <Typography variant="h3">{formatNumber(timeLeft.hours)}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {t('maintenance.hours')}
                  </Typography>
                </TimeBlock>
                <TimeBlock>
                  <Typography variant="h3">{formatNumber(timeLeft.minutes)}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {t('maintenance.minutes')}
                  </Typography>
                </TimeBlock>
                <TimeBlock>
                  <Typography variant="h3">{formatNumber(timeLeft.seconds)}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {t('maintenance.seconds')}
                  </Typography>
                </TimeBlock>
              </CountdownBox>
            </>
          )}
        </Container>
      </RootStyle>
    </Page>
  );
}
